/**
 * Who is signed in. LGU staff and merchant users sign in with e-mail + password, then
 * an authenticator code where their role requires it. The first sign-in asks for a new
 * password and (where required) authenticator enrolment before anything else.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';
import { useI18n, type Lang } from './i18n';

export type Role =
  | 'SUPER_ADMIN' | 'LGU_ADMIN' | 'EXECUTIVE' | 'REGISTRATION' | 'CARD_STAFF' | 'SOCIAL_WORKER' | 'PROGRAM_HEAD'
  | 'APPROVER' | 'TREASURER' | 'ACCOUNTING' | 'AUDITOR' | 'MERCHANT_OWNER' | 'CASHIER';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  language: Lang;
  roles: Role[];
  merchantId: string | null;
  permissions: string[];
  mfaEnabled: boolean;
  mfaRequired: boolean;
  mustChangePassword: boolean;
}

export interface SessionResponse { accessToken: string; user: SessionUser }

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  adopt: (r: SessionResponse) => void;
  reissue: () => Promise<SessionUser>;
  signOut: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
  /** Where this user works: the LGU office, a merchant's portal, or only the POS. */
  area: 'office' | 'merchant' | 'pos' | null;
}

const Ctx = createContext<AuthState | null>(null);

export function areaOf(u: SessionUser | null): AuthState['area'] {
  if (!u) return null;
  if (!u.merchantId) return 'office';
  return u.roles.includes('MERCHANT_OWNER') ? 'merchant' : 'pos';
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  const { setLang } = useI18n();

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  const adopt = useCallback((r: SessionResponse) => {
    setAccessToken(r.accessToken);
    setUser(r.user);
    if (r.user.language) setLang(r.user.language);
  }, [setLang]);

  useEffect(() => {
    setUnauthenticatedHandler(clear);
    void fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' })
      .then(async (res) => (res.ok ? adopt((await res.json()) as SessionResponse) : undefined))
      .catch(() => undefined)
      .finally(() => setLoading(false));
    return () => setUnauthenticatedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reissue = useCallback(async () => {
    const r = await api<SessionResponse>('/auth/reissue', { method: 'POST', body: {} });
    adopt(r);
    return r.user;
  }, [adopt]);

  const signOut = useCallback(async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: '{}' }).catch(() => undefined);
    clear();
  }, [clear]);

  const value = useMemo<AuthState>(() => ({
    user, loading, adopt, reissue, signOut,
    can: (...perms) => !!user && perms.some((p) => user.permissions.includes(p)),
    area: areaOf(user),
  }), [user, loading, adopt, reissue, signOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
